import React from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Skeleton,
} from '@mui/material';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';
import { DealType } from '../kanban/DealsKanban.types';

interface DealTypeSelectorProps {
  dealTypes: { [id: string]: DealType };
  categoryId: string;
  onCategoryChange: (categoryId: string, categoryTitle: string) => void;
  isLoading?: boolean;
}

const DealTypeSelector: React.FC<DealTypeSelectorProps> = ({
  dealTypes,
  categoryId,
  onCategoryChange,
  isLoading = false,
}) => {
  const { theme } = useTheme();

  if (Object.keys(dealTypes).length <= 1) {
    return null;
  }

  const dealTypeOptions = Object.values(dealTypes).map((dt: DealType) => ({
    label: dt.title,
    value: dt.id,
  }));

  const selectedValue = Object.values(dealTypes).find(
    (dt: DealType) => dt.id === categoryId
  )
    ? {
        label: dealTypes[categoryId].title,
        value: categoryId,
      }
    : null;

  return (
    <Box className={'deal-list-settings-container'}>
      <Typography
        className={'deal-settings-title-text'}
        sx={{ color: theme.text }}
      >
        Type
      </Typography>
      {isLoading ? (
        <Box
          className={'deal-settings-dropdown-skeleton-container'}
          sx={{ backgroundColor: theme.background1 }}
        >
          <Skeleton variant={'rounded'} width={'100%'} height={23}></Skeleton>
        </Box>
      ) : (
        <Box>
          <Autocomplete
            id={'deal-type-autocomplete'}
            classes={{
              inputRoot: 'scorecard-dropdown-box',
              paper: 'autocomplete-menu',
            }}
            sx={{
              width: 270,
              '& .MuiOutlinedInput-root': {
                backgroundColor: `${theme.background1} !important`,
                color: `${theme.text} !important`,
                '& fieldset': {
                  borderColor: theme.stroke,
                },
                '&:hover fieldset': {
                  borderColor: theme.stroke,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.primary,
                },
              },
              '& .MuiInputBase-input': {
                backgroundColor: `${theme.background1} !important`,
                color: `${theme.text} !important`,
              },
              '& .MuiAutocomplete-input': {
                color: `${theme.text} !important`,
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: `${theme.stroke} !important`,
              },
              '& .MuiSvgIcon-root': {
                color: `${theme.text} !important`,
              },
            }}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: theme.background1,
                  '& .MuiAutocomplete-option': {
                    color: theme.text,
                    '&:hover': {
                      backgroundColor: theme.primaryLight,
                    },
                    '&.Mui-focused': {
                      backgroundColor: theme.background3,
                    },
                  },
                },
              },
            }}
            onChange={(e, val) => {
              if (val && categoryId !== val.value) {
                onCategoryChange(val.value, val.label);
              }
            }}
            options={dealTypeOptions}
            value={selectedValue}
            renderInput={(params) => <TextField {...params} />}
          />
        </Box>
      )}
    </Box>
  );
};

export default DealTypeSelector;
